import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ArrowLeft, Copy, Pencil, Link2, Send } from 'lucide-react';
import { API, StatusBadge, fmtDate, cardCls, txtMain, txtSub } from './nlShared';
import { LoadingSpinner } from '../core';
import { toast } from 'sonner';

const KPIS = [
  ['sent', 'Envoyés'], ['delivered', 'Délivrés'], ['opens', 'Ouvertures'], ['unique_opens', 'Ouvertures uniques'],
  ['clicks', 'Clics'], ['unique_clicks', 'Clics uniques'], ['open_rate', 'Taux d\'ouverture', '%'], ['ctr', 'CTR', '%'],
];

export function NlDetail({ theme, campaignId, onExit, onEdit }) {
  const light = theme === 'light';
  const [c, setC] = useState(null);
  const [resuming, setResuming] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/nl/campaigns/${campaignId}`, { withCredentials: true }).then(r => setC(r.data)).catch(() => onExit());
  }, [campaignId, onExit]);

  if (!c) return <LoadingSpinner />;
  const st = c.stats || {};
  const tl = st.timeline || [];
  const maxTl = Math.max(1, ...tl.map(d => Math.max(d.opens, d.clicks)));

  const duplicate = async () => {
    const { data } = await axios.post(`${API}/admin/nl/campaigns/${campaignId}/duplicate`, {}, { withCredentials: true });
    toast.success('Campagne dupliquée');
    onEdit(data.id);
  };
  const resumeSend = async () => {
    if (!window.confirm('Reprendre l\'envoi ? Seuls les destinataires n\'ayant pas encore reçu l\'email le recevront (aucun doublon).')) return;
    setResuming(true);
    try {
      await axios.post(`${API}/admin/nl/campaigns/${campaignId}/send`, { when: 'now' }, { withCredentials: true });
      toast.success('Reprise de l\'envoi lancée — les destinataires déjà servis sont ignorés');
      const { data } = await axios.get(`${API}/admin/nl/campaigns/${campaignId}`, { withCredentials: true });
      setC(data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setResuming(false);
  };

  return (
    <div data-testid="nl-detail">
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <button data-testid="nl-detail-back" onClick={onExit} className={`p-2 rounded-lg ${light ? 'hover:bg-gray-100 text-gray-500' : 'hover:bg-white/10 text-white/50'}`}><ArrowLeft size={18} /></button>
        <div className="flex-1 min-w-0">
          <h2 className={`text-xl font-bold truncate ${txtMain(light)}`}>{c.name}</h2>
          <p className={`text-xs ${txtSub(light)}`}>{c.subject || 'Sans objet'} · {c.author} · {fmtDate(c.sent_at || c.updated_at)} · Segments : {(c.segments || (c.segment ? [c.segment] : [])).map(s => s.label).join(' + ') || '—'}</p>
        </div>
        <StatusBadge status={c.status} />
        {(c.status === 'error' || c.status === 'sending') && (
          <button data-testid="nl-detail-resume" onClick={resumeSend} disabled={resuming} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-green-600 text-white hover:bg-green-500 disabled:opacity-50">
            <Send size={13} /> {resuming ? 'Reprise…' : 'Reprendre l\'envoi'}
          </button>
        )}
        {c.status !== 'sent' && c.status !== 'sending' && (
          <button data-testid="nl-detail-edit" onClick={() => onEdit(c.id)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg bg-[#E9641A] text-white hover:bg-[#FE975E]"><Pencil size={13} /> Modifier</button>
        )}
        <button data-testid="nl-detail-dup" onClick={duplicate} className={`flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg ${light ? 'bg-gray-100 text-gray-700' : 'bg-white/8 text-white'}`}><Copy size={13} /> Dupliquer</button>
      </div>

      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-12 lg:col-span-7 space-y-5">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {KPIS.map(([k, label, suffix]) => (
              <div key={k} className={`${cardCls(light)} p-3.5`} data-testid={`nl-kpi-${k}`}>
                <p className={`text-lg font-bold ${txtMain(light)}`}>{(st[k] ?? 0).toLocaleString('fr-FR')}{suffix || ''}</p>
                <p className={`text-[10px] uppercase tracking-wide ${txtSub(light)}`}>{label}</p>
              </div>
            ))}
          </div>
          {tl.length > 0 && (
            <div className={`${cardCls(light)} p-5`} data-testid="nl-detail-chart">
              <p className={`text-sm font-bold mb-4 ${txtMain(light)}`}>Évolution ouvertures / clics</p>
              <div className="flex items-end gap-1 h-28">
                {tl.map((d, i) => (
                  <div key={i} className="flex-1 h-full flex flex-col justify-end gap-[1px]" title={`${d.date} · ${d.opens} ouv. · ${d.clicks} clics`}>
                    <div className="bg-[#E9641A]/70 rounded-t" style={{ height: `${Math.max(2, (d.opens / maxTl) * 46)}%` }} />
                    <div className="bg-purple-500/60 rounded-t" style={{ height: `${Math.max(2, (d.clicks / maxTl) * 46)}%` }} />
                  </div>
                ))}
              </div>
              <div className={`flex gap-4 mt-2 text-[10px] ${txtSub(light)}`}>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-[#E9641A]/70 rounded" />Ouvertures</span>
                <span className="flex items-center gap-1"><span className="w-2 h-2 bg-purple-500/60 rounded" />Clics</span>
              </div>
            </div>
          )}
          {(st.top_links || []).length > 0 && (
            <div className={`${cardCls(light)} p-5`} data-testid="nl-detail-links">
              <p className={`text-sm font-bold mb-3 ${txtMain(light)}`}><Link2 size={14} className="inline mr-1 text-[#E9641A]" />Liens les plus cliqués</p>
              {st.top_links.map((l, i) => (
                <div key={i} className={`flex items-center gap-3 py-2 border-b last:border-0 ${light ? 'border-gray-100' : 'border-white/5'}`}>
                  <span className={`text-xs truncate flex-1 ${light ? 'text-gray-600' : 'text-white/70'}`}>{l.url}</span>
                  <span className="text-xs font-bold text-[#E9641A]">{l.clicks} clic{l.clicks > 1 ? 's' : ''}</span>
                </div>
              ))}
            </div>
          )}
          {c.last_error && <div className="rounded-lg bg-red-500/10 border border-red-500/30 p-3 text-xs text-red-400" data-testid="nl-detail-error">Dernière erreur : {c.last_error}</div>}
        </div>
        <div className="col-span-12 lg:col-span-5">
          <div className={`${cardCls(light)} overflow-hidden`} data-testid="nl-detail-preview">
            <p className={`text-xs font-bold px-4 py-2.5 border-b ${light ? 'border-gray-200' : 'border-white/10'} ${txtMain(light)}`}>Aperçu de la newsletter</p>
            <iframe title="Aperçu" src={`${API}/admin/nl/campaigns/${campaignId}/preview`} className="w-full h-[560px]" style={{ background: '#0b0612' }} />
          </div>
        </div>
      </div>
    </div>
  );
}
